import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AccountService } from '@app/core/services/client/account.service';
import { AccountComponent } from './modals/account/account.component';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@app/auth/store/auth.selectors';
import { LoadingService } from '@app/core/services/loading.service';
import { AccountDetailsComponent } from './modals/account-details/account-details.component';
import { AccountTypeComponent } from './modals/create-account/account-type/account-type.component';
import { ProfileDetailsComponent } from './modals/profile-details/profile-details.component';
import { ProfieDetails } from '@app/core/models/client/account/account-details.model';
import { InvestmentPlanComponent } from '@app/investment/investment-plan/investment-plan.component';
import { InvestmentTypeComponent } from '@app/liquidate/investment-type/investment-type.component';
import { WithdrawComponent } from '@app/withdraw/withdraw.component';
import { environment } from '@app/core/environments/environment';
import { IDoc } from '@app/core/models/client/account/account-details.model';
import { IAccountStateModel } from '@app/auth/store/auth.state.models';
import { AuthActions } from '@app/auth/store/auth.actions';
import { PortfolioActions } from '@app/portfolio/store/portfolio.action';
import { NotificationFacade } from '@app/core/facades/notification.facade';
import { Clipboard } from '@angular/cdk/clipboard';
import { EditProfilePictureComponent } from './modals/edit-profile-picture/edit-profile-picture';
import { HelpDeskModalComponent } from './modals/help-desk/help-desk-modal.component';
import { NotificationsModalComponent } from './modals/notifications/notifications-modal.component';
import { PushNotificationService } from '@app/core/services/client/push-notification.service';
import { NotificationStoreService } from '@app/core/services/client/notification-store.service';


@Component({
  selector: 'app-global-header',
  standalone: true,
  imports: [
    CommonModule,
    AccountComponent,
    AccountDetailsComponent,
    AccountTypeComponent,
    ProfileDetailsComponent,
    InvestmentPlanComponent,
    InvestmentTypeComponent,
    WithdrawComponent,
    EditProfilePictureComponent,
    HelpDeskModalComponent,
    NotificationsModalComponent
  ],
  templateUrl: './global-header.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalHeaderComponent implements OnInit, OnDestroy {
  router = inject(Router);
  store = inject(Store);
  accountService = inject(AccountService);
  loadingService = inject(LoadingService);
  change = inject(ChangeDetectorRef);
  notify = inject(NotificationFacade);
  clipboard = inject(Clipboard);
  push = inject(PushNotificationService);
  notifications = inject(NotificationStoreService);

  unreadCount = this.notifications.unreadCount;

  subs: Subscription[] = [];
  kycDetails?: ProfieDetails;
  openModal: string | null = null;
  isAccountModalOpen: boolean = false;
  isInvestmentPlanModalOpen: boolean = false;
  isInvestmentTypeModalOpen: boolean = false;
  isAccountDetailsModalOpen: boolean = false;
  isportfolioManagementModalOpen: boolean = false;
  isWithdrawModalOpen: boolean = false;
  tierLevelSubject$ = new BehaviorSubject<string>('');
  tierLevel$ = this.tierLevelSubject$.asObservable();
  isFetchingTierLevel$!: Observable<boolean>;

  profileID = this.store.selectSnapshot(AuthSelectors.getProfileID);
  accountID = this.store.selectSnapshot(AuthSelectors.getActiveAccountID);
  activeAccount?: IAccountStateModel;
  accountList = this.store.selectSnapshot(AuthSelectors.getAllAccounts);
  activeAccount$ = this.store.select(AuthSelectors.getActiveAccount);

  constructor() {
    this.isFetchingTierLevel$ = this.loadingService.getLoadingObservable(
      'check-kyc-tier-level'
    );
  }
  ngOnInit(): void {
    // this.getProfileAccounts(this.profileID as string);
    // this.getAccountKycTier();

    this.push.refreshTokenIfGranted();

    this.notifications.refresh();

    // Nudge clients who haven't enabled push notifications yet
    this.maybePromptEnablePush();

    this.push.onForegroundMessage((payload) => {
      const n = payload?.notification || {};
      const d = payload?.data || {};
      const title = n.title || d.title || 'Notification';
      const body = n.body || d.body || d.message || '';
      this.notifications.refresh();
      this.notify.info(body, title);
      this.change.markForCheck();
    });

    this.store.dispatch(new AuthActions.EnumsList());
    const sub = this.activeAccount$.subscribe((account) => {
      if (account) {
        console.log('Account Type is:', account.account_type, 'Account Data-:', account);
        this.activeAccount = account;
        let passportObj: any;
        if (Array.isArray(account.doc)) {
          passportObj = account.doc.filter(
            (doc: IDoc) => doc.document_type === 'passport_photograph'
          )[0];
        }
        this.kycDetails = {
          name: `${account.user_profile.first_name || ''} ${account.user_profile.last_name || ''}`.trim(),
          email: account.user_profile.email || '',
          phone_number: account.user_profile.phone_number_1 || '',
          bvn: account.user_profile?.bvn || '',
          country: account?.location_kyc?.country || '',
          state: account?.location_kyc?.state || '',
          city: account?.location_kyc?.city || '',
          passportPhotoUrl: `${passportObj?.link ? environment.FILE_BASE_URL + passportObj.link : ''}`,
          userUploadedPhotoUrl: account.user_profile.profile_image
            ? `${environment.FILE_BASE_URL}${account.user_profile.profile_image}`
            : '',
        };
        this.change.detectChanges();
      }

      this.store.dispatch(PortfolioActions.GetNGNStats);
      this.store.dispatch(PortfolioActions.GetUSDStats);
      this.change.detectChanges();
    });
    this.subs.push(sub);
  }

  private maybePromptEnablePush(): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;

    const toast = this.notify.info(
      'Get instant updates on everything Mamvest.',
      'Turn on Notifications',
      {
        closeButton: true,
        tapToDismiss: false,
        disableTimeOut: true,
        actionLabel: 'Enable',
      } as any
    );

    const actionSub = toast?.onAction?.subscribe(async () => {
      // Dismiss the prompt as soon as the user acts on it.
      toast?.toastRef?.manualClose();
      await this.push.requestPermissionAndRegister();
      if (Notification.permission === 'granted') {
        this.notifications.refresh();
        this.notify.success('Notifications enabled successfully');
        window.location.reload();
        return;
      } else if (Notification.permission === 'denied') {
        this.notify.error(
          'Notifications are blocked. You can enable them in your browser settings.'
        );
      }
      this.change.markForCheck();
    });
    if (actionSub) this.subs.push(actionSub);
  }

  toggleAccountModal(): void {
    this.isAccountModalOpen = !this.isAccountModalOpen;
  }

  // investing
  toggleInvestmentPlanModal(): void {
    this.isInvestmentPlanModalOpen = !this.isInvestmentPlanModalOpen;
  }

  // Liquidating
  toggleInvestmentTypeModal(): void {
    this.isInvestmentTypeModalOpen = !this.isInvestmentTypeModalOpen;
  }

  // Withdraw
  toggleWithdrawModal(): void {
    this.isWithdrawModalOpen = !this.isWithdrawModalOpen;
  }

  handleModalEvent(modalName: string): void {
    // Close all modals first
    this.isAccountModalOpen = false;
    this.isInvestmentPlanModalOpen = false;
    this.isInvestmentTypeModalOpen = false;
    this.isWithdrawModalOpen = false;
    this.openModal = null;

    // Open the requested modal
    if (this.openModal !== modalName) {
      this.openModal = modalName;
    }
  }

  toggleHelpDeskModal(): void {
    this.openModal = 'helpDesk';
  }

  toggleNotificationsModal(): void {
    this.openModal = 'notifications';
  }

  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      if (i > 0) {
        return (
          p +
          ` & ${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`
        );
      }
      return `${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`;
    }, '');
  }

  copy(accountNumber: string) {
    this.clipboard.copy(accountNumber);
    this.notify.success('Successfully copied to clipboard');
  }

  imageVersion = Date.now()
  onPhotoChanged(newPhotoUrl: string): void {
    if (!this.kycDetails) return;
    this.imageVersion = Date.now();

    this.kycDetails = {
      ...this.kycDetails,
      userUploadedPhotoUrl: `${newPhotoUrl}?v=${this.imageVersion}`, //To force reload the image so that it updates without refreshing the browser
    };

    this.change.markForCheck();
    this.notify.success('Your profile picture has been changed successfully');
  }

  navigate(page: string): void {
    this.router.navigate([`/${page}`]);
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
